import React, { useMemo } from 'react';
import {
  UserCircle, UserCircleCheck, UserCircleMinus, Warning,
} from '@phosphor-icons/react';
import BatchImportModal from '../components/BatchImportModal';
import MergeDialog from '../components/MergeDialog';
import NeedsCompletionBanner from '../components/shared/NeedsCompletionBanner';
import { EmployeeFormDialog } from '../components/employees/EmployeeFormDialog';
import { EmployeesTable } from '../components/employees/EmployeesTable';
import { StatsCardGrid } from '../components/shared/StatsCardGrid';
import { EmployeesPageHeader } from './employees/EmployeesPageHeader';
import { EmployeesBulkActionBar } from './employees/EmployeesBulkActionBar';
import { EmployeesDuplicatesPanel } from './employees/EmployeesDuplicatesPanel';
import { EmployeesFilters } from './employees/EmployeesFilters';
import { useEmployees } from './employees/useEmployees';

/**
 * Employees page — thin orchestrator. State + handlers live in
 * `useEmployees`; presentational components live in `pages/employees/`.
 */
const EmployeesPage = () => {
  const c = useEmployees();

  const needsCompletionCount = useMemo(
    () => c.employees.filter((e) => e.status === 'Needs completion').length,
    [c.employees]
  );

  const statsCards = useMemo(() => [
    {
      label: 'Total', value: c.employees.length, icon: UserCircle, color: 'indigo',
      filter: '', testid: 'stat-card-total',
    },
    {
      label: 'Active',
      value: c.employees.filter((e) => e.status === 'Active').length,
      icon: UserCircleCheck, color: 'emerald', filter: 'Active', testid: 'stat-card-active',
    },
    {
      label: 'Needs completion',
      value: needsCompletionCount,
      icon: Warning, color: 'amber', filter: 'Needs completion',
      testid: 'stat-card-needs-completion',
    },
    {
      label: 'Inactive',
      value: c.employees.filter((e) => e.status === 'Inactive').length,
      icon: UserCircleMinus, color: 'slate', filter: 'Inactive', testid: 'stat-card-inactive',
    },
  ], [c.employees, needsCompletionCount]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="employees-page">
      <EmployeesPageHeader
        scanningDuplicates={c.scanningDuplicates}
        onScanDuplicates={c.handleScanDuplicates}
        onImport={() => c.setImportModalOpen(true)}
        onAddEmployee={() => c.handleOpenModal()}
      />

      <NeedsCompletionBanner
        count={needsCompletionCount}
        recordLabel="employee"
        onClickComplete={() => c.setFilterStatus('Needs completion')}
      />

      <EmployeesBulkActionBar
        selectedCount={c.selectedIds.length}
        onClear={() => c.setSelectedIds([])}
        onBulkDelete={c.handleBulkDelete}
        bulkDeleting={c.bulkDeleting}
      />

      <EmployeesDuplicatesPanel
        duplicates={c.duplicates}
        show={c.showDuplicates}
        onDismiss={() => c.setShowDuplicates(false)}
        onMerge={c.setMergeGroup}
      />

      <EmployeesFilters
        search={c.search}
        onSuggestSelect={c.handleSuggestSelect}
        filterStatus={c.filterStatus}
        onFilterStatus={c.setFilterStatus}
        filterJobTitle={c.filterJobTitle}
        onFilterJobTitle={c.setFilterJobTitle}
      />

      <StatsCardGrid
        columns={4}
        activeFilter={c.filterStatus}
        onFilterChange={c.setFilterStatus}
        cards={statsCards}
      />

      <EmployeesTable
        loading={c.loading}
        filteredEmployees={c.filteredEmployees}
        selectedIds={c.selectedIds}
        isAllSelected={c.isAllSelected}
        isSomeSelected={c.isSomeSelected}
        toggleSelectAll={c.toggleSelectAll}
        toggleSelect={c.toggleSelect}
        getStatusBadge={c.getStatusBadge}
        onOpenModal={c.handleOpenModal}
        onActivate={c.handleActivate}
        onDeactivate={c.handleDeactivate}
        onTerminate={c.handleTerminate}
        onDelete={c.handleDelete}
      />

      <EmployeeFormDialog
        open={c.isModalOpen}
        onOpenChange={c.handleCloseModal}
        formData={c.formData}
        onChange={c.handleChange}
        onSubmit={c.handleSubmit}
        editing={!!c.editingEmployee}
        saving={c.saving}
      />

      <BatchImportModal
        isOpen={c.importModalOpen}
        onClose={() => c.setImportModalOpen(false)}
        importType="employee"
        onImportComplete={c.fetchEmployees}
      />

      <MergeDialog
        isOpen={!!c.mergeGroup}
        onClose={() => c.setMergeGroup(null)}
        mergeType="employee"
        group={c.mergeGroup}
        onMergeComplete={() => { c.fetchEmployees(); c.handleScanDuplicates(); }}
      />
    </div>
  );
};

export default EmployeesPage;
